import React, { useState } from 'react';
import FullscreenViewer from './FullscreenViewer';
import { AuthenticatedImage } from './AuthenticatedImage';
import { AuthenticatedVideo, isVideoArtifact } from './AuthenticatedVideo';
import { humanisePhase, parseSubProgress } from './ProgressIndicator';
import { getApiBaseUrl } from '../../config/environment';

const isAppleTouchDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isIpadOsDesktopUa = platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  return isIOS || isIpadOsDesktopUa;
};

interface ImageViewerProps {
  selectedImage: {
    jobId: string;
    workflowId: string;
    workflowName: string;
    createdAt: string;
    artifacts: Array<{
      filename: string;
      type: string;
    }>;
    thumbnailUrl?: string;
  } | null;
  currentJob: {
    jobId: string;
    status: 'queued' | 'claimed' | 'processing' | 'completed' | 'failed' | 'pending' | 'running';
    progress: number;
    stage: string;
    message: string;
    currentStep?: number;
    totalSteps?: number;
  } | null;
  accessToken: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ selectedImage, currentJob, accessToken }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prefersNativeSaveSheet = isAppleTouchDevice();

  // Get image URL with auth token
  const getImageUrl = (jobId: string, filename: string): string => {
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/api/jobs/${jobId}/artifacts/${filename}`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Download image
  const handleDownload = async () => {
    const artifact = selectedImage?.artifacts[0];
    if (!selectedImage || !artifact) return;

    const url = getImageUrl(selectedImage.jobId, artifact.filename);
    
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      const filename = artifact.filename;
      const file = new File([blob], filename, {
        type: blob.type || artifact.type || 'application/octet-stream'
      });

      const canUseNativeShare =
        prefersNativeSaveSheet &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

      if (canUseNativeShare) {
        try {
          await navigator.share({
            files: [file],
            title: filename,
          });
          return;
        } catch (shareErr) {
          if (shareErr instanceof DOMException && shareErr.name === 'AbortError') {
            return;
          }
          console.warn('Native share failed, falling back to direct download:', shareErr);
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      if (prefersNativeSaveSheet) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 60_000);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Empty state
  if (!selectedImage && !currentJob) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-subtle m-2 sm:m-4 rounded-xl border border-gray-200 p-6">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-700 mb-1">Start by choosing what to create</h3>
          <p className="text-body text-gray-400">Select a workflow and hit Create</p>
        </div>
      </div>
    );
  }

  // Generating state — atmospheric placeholder only.
  // All numerical progress (phase + clock + %) lives in the TopBar's
  // <ProgressIndicator/>; the central panel just signals "something is
  // arriving here" via a quiet shimmer over an editorial placeholder card.
  // Covers the whole active lifecycle (queued → running) so the placeholder
  // appears the moment the user clicks Generate, even before the LocalAgent
  // has claimed the job from the Azure queue.
  const isJobActive = currentJob &&
    currentJob.status !== 'completed' && currentJob.status !== 'failed';
  if (isJobActive) {
    const phaseLabel = humanisePhase(currentJob!.stage || 'queued');
    // Structured steps (v2 agents) with message-parse fallback (v1 agents)
    const sub = (currentJob!.currentStep !== undefined && (currentJob!.totalSteps || 0) > 0)
      ? { value: currentJob!.currentStep!, max: currentJob!.totalSteps! }
      : (currentJob!.stage === 'generating' ? parseSubProgress(currentJob!.message) : null);
    const heading = sub ? `${phaseLabel} · step ${sub.value} / ${sub.max}` : phaseLabel;

    return (
      <div className="flex-1 flex items-center justify-center bg-surface-subtle m-2 sm:m-4 rounded-xl border border-gray-200 p-5 sm:p-8">
        <div className="w-full max-w-lg text-center">
          {/* Placeholder canvas — slow breathe + soft horizontal shimmer */}
          <div className="ccs-placeholder relative w-full aspect-[4/3] mb-5 rounded-xl bg-gray-100 border border-gray-200 overflow-hidden shadow-inner">
            <div className="ccs-shimmer absolute inset-0" />
          </div>
          <div className="max-w-sm mx-auto">
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-blue-500 transition-all duration-300 ease-out" style={{ width: `${currentJob!.progress || 0}%` }} />
            </div>
            <p className="text-label uppercase tracking-wide text-gray-500">{heading}</p>
            <p className="text-sm text-gray-400 mt-1">
              {currentJob!.progress || 0}% complete{currentJob!.message && !sub ? ` · ${currentJob!.message}` : ''}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Media display — image or video, picked from the first artifact
  const primaryArtifact = selectedImage?.artifacts[0];
  const mediaUrl = selectedImage && primaryArtifact ? getImageUrl(selectedImage.jobId, primaryArtifact.filename) : '';
  const isVideo = !!primaryArtifact && (isVideoArtifact(primaryArtifact.filename) || isVideoArtifact(primaryArtifact.type));

  return (
    <>
      <div className="flex-1 flex flex-col bg-surface-panel m-2 sm:m-4 rounded-xl shadow-md border border-gray-200 overflow-hidden min-h-0">
        {/* Media — clicking still images opens fullscreen; videos handle their own controls */}
        <div className="flex-1 flex items-center justify-center bg-surface-subtle p-3 sm:p-6 overflow-hidden min-h-0">
          {isVideo ? (
            <AuthenticatedVideo
              src={mediaUrl}
              accessToken={accessToken}
              controls
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-sm bg-black"
            />
          ) : (
            <button
              type="button"
              onClick={() => setIsFullscreen(true)}
              className="relative max-w-full max-h-full group focus:outline-none"
              title="View fullscreen"
            >
              <AuthenticatedImage
                src={mediaUrl}
                alt="Generated content"
                accessToken={accessToken}
                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-sm group-hover:opacity-95 transition-opacity duration-fast"
              />
              {/* Fullscreen hint overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors duration-fast rounded-lg">
                <div className="opacity-0 group-hover:opacity-100 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-opacity duration-fast">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  Fullscreen
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Info bar */}
        {selectedImage && (
          <div className="border-t border-gray-200 bg-surface-panel px-6 py-3 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-label text-gray-500 min-w-0 overflow-hidden">
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="whitespace-nowrap">{formatDate(selectedImage.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  <span className="font-mono text-meta">{selectedImage.jobId.substring(0, 12)}…</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDownload}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md shadow-sm transition-colors duration-fast whitespace-nowrap flex-shrink-0 touch-target"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                {prefersNativeSaveSheet ? 'Save' : 'Download'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Viewer — only for still images; videos play inline */}
      {isFullscreen && selectedImage && !isVideo && (
        <FullscreenViewer
          imageUrl={mediaUrl}
          accessToken={accessToken}
          onClose={() => setIsFullscreen(false)}
        />
      )}
    </>
  );
};

export default ImageViewer;

